import {memo, useCallback} from "react";

import {type CalendarDay} from "@/shared/models/form/datepickers";

import {CalendarDayItem} from "./calendar-day-item";
import {useDayTooltip} from "./use-day-tooltip";

export type {CalendarDay};

interface CalendarDaysProps {
    days: ReadonlyArray<CalendarDay>;
    onDayClick: (day: CalendarDay) => void;
    onDayHover: (date: Date) => void;
    onDayLeave: () => void;
    getDayTooltip?: (date: Date) => string | null | undefined;
    getDayClassName?: (date: Date) => string | null | undefined;
    hideOtherMonthDaysContent?: boolean;
}

export const CalendarDays = memo(function CalendarDays({
    days,
    onDayClick,
    onDayHover,
    onDayLeave,
    getDayTooltip,
    getDayClassName,
    hideOtherMonthDaysContent,
}: CalendarDaysProps) {
    const {tooltip, tooltipPosition, containerRef, tooltipRef, showTooltip, hideTooltip, toggleTooltip} = useDayTooltip({getDayTooltip});

    const handleDayHover = useCallback(
        (day: CalendarDay, isDisabled: boolean, index: number, element: HTMLDivElement) => {
            showTooltip(day.date, element);

            // For disabled days - clear hover range by calling onDayLeave
            if (isDisabled) {
                onDayLeave();
            } else {
                onDayHover(day.date);
            }
        },
        [showTooltip, onDayHover, onDayLeave],
    );

    const handleMouseLeave = useCallback(() => {
        hideTooltip();
        onDayLeave();
    }, [hideTooltip, onDayLeave]);

    const handleDayClick = useCallback(
        (day: CalendarDay, element: HTMLDivElement) => {
            // Show tooltip on click (for mobile)
            toggleTooltip(day.date, element);
            onDayClick(day);
        },
        [toggleTooltip, onDayClick],
    );

    return (
        <div ref={containerRef} className="daterange-picker-field__days" onMouseLeave={handleMouseLeave}>
            {/* Tooltip rendered once at container level */}
            {tooltip && tooltipPosition && (
                <div
                    ref={tooltipRef}
                    className={`daterange-picker-field__day-tooltip daterange-picker-field__day-tooltip--${tooltipPosition.placement}`}
                    style={{
                        position: "absolute",
                        top: tooltipPosition.top,
                        left: tooltipPosition.left,
                        transform: tooltipPosition.placement === "top" ? "translate(-50%, -100%)" : "translate(-50%, 40px)",
                        zIndex: 10,
                    }}
                >
                    {tooltip}
                </div>
            )}
            {days.map((day, index) => {
                const isDisabled = day.isDisabled || !day.isCurrentMonth;
                const additionalClassName = getDayClassName ? getDayClassName(day.date) : null;
                return (
                    <CalendarDayItem
                        key={index}
                        day={day}
                        index={index}
                        isDisabled={isDisabled}
                        additionalClassName={additionalClassName}
                        onDayClick={handleDayClick}
                        onMouseEnter={handleDayHover}
                        hideOtherMonthDaysContent={hideOtherMonthDaysContent}
                    />
                );
            })}
        </div>
    );
});
